// Aba "📥 Recebidos por e-mail" do Controle de Notas: o que chegou na caixa
// nucleo.adm2@vitalcarehc.com.br — prévias (equipamentos, remoções, gases…), notas fiscais e
// vencimentos, relatórios. Os registros são gravados pelo servidor (api/_controle-notas-email.js).
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { db, getCollectionName } from '../firebase';
import { collection, getDocs } from '../firestore-compat';
import { supabase } from '../supabase';
import { ArquivoLink } from '../arquivos';

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', primary: '#6366f1' };
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const fmtMes = m => (m ? `${MESES[Number(m.slice(5, 7)) - 1]}/${m.slice(0, 4)}` : '—');
const fmtData = iso => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('pt-BR') : '—');
const fmtValor = v => (typeof v === 'number' ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—');
const DIA = 864e5;
function prazo(iso) {
  if (!iso) return null;
  const h = new Date(); h.setHours(0, 0, 0, 0);
  const d = Math.round((new Date(`${iso}T00:00:00`) - h) / DIA);
  if (d < 0) return { d, rotulo: `Vencido há ${-d} dia${d === -1 ? '' : 's'}`, cor: '#991b1b', fundo: '#fee2e2' };
  if (d === 0) return { d, rotulo: 'Vence hoje', cor: '#9a3412', fundo: '#ffedd5' };
  if (d <= 7) return { d, rotulo: `Vence em ${d} dia${d === 1 ? '' : 's'}`, cor: '#9a3412', fundo: '#ffedd5' };
  if (d <= 30) return { d, rotulo: `Vence em ${d} dias`, cor: '#854d0e', fundo: '#fef9c3' };
  return { d, rotulo: `Vence em ${d} dias`, cor: '#166534', fundo: '#dcfce7' };
}
const chip = (fundo, cor) => ({ fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap', display: 'inline-block' });
const TIPO_ROTULO = { previa: 'Prévia', nota: 'Nota fiscal', cobranca: 'Boleto / cobrança', relatorio: 'Relatório' };
const FOCO = ['Gases e equipamentos', 'Equipamentos', 'Remoções', 'Mobiliário', 'Raio-X'];

function Anexos({ lista }) {
  const docs = (lista || []).filter(a => a.url);
  if (!docs.length) return <span style={{ color: '#94a3b8' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {docs.slice(0, 6).map((a, i) => (
        <ArquivoLink key={i} href={a.url} target="_blank" rel="noreferrer" title={a.nome}
          style={{ fontSize: '0.72rem', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '2px 6px', textDecoration: 'none', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📎 {a.nome}
        </ArquivoLink>
      ))}
      {docs.length > 6 && <span style={{ fontSize: '0.72rem', color: P.muted }}>+{docs.length - 6}</span>}
    </div>
  );
}

export default function RecebidosEmail() {
  const [emails, setEmails] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [visao, setVisao] = useState('previas');
  const [tipoForn, setTipoForn] = useState('foco');
  const [filial, setFilial] = useState('');
  const [mes, setMes] = useState('');
  const [busca, setBusca] = useState('');
  const [atualizando, setAtualizando] = useState('');
  const [verPagos, setVerPagos] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const snap = await getDocs(collection(db, getCollectionName('controladoria_emails')));
      setEmails(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.warn('[RecebidosEmail]', e.message); }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, []);

  const atualizarAgora = async () => {
    setAtualizando('Lendo a caixa de e-mail…');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/controle-notas-emails', { method: 'POST', headers: { Authorization: `Bearer ${session?.access_token || ''}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
      setAtualizando(`${j.gravados || 0} e-mail(s) novo(s)`);
      await carregar();
    } catch (e) { setAtualizando('Não foi possível: ' + e.message); }
    setTimeout(() => setAtualizando(''), 5000);
  };

  const q = busca.trim().toLowerCase();
  const base = useMemo(() => emails.filter(e =>
    (tipoForn === '' || (tipoForn === 'foco' ? FOCO.includes(e.tipoFornecedor) : e.tipoFornecedor === tipoForn))
    && (!filial || e.filial === filial || e.filial === 'Ambas')
    && (!mes || e.mesRef === mes)
    && (!q || `${e.fornecedor} ${e.assunto} ${e.nfAssunto} ${(e.notas || []).map(n => n.numero).join(' ')}`.toLowerCase().includes(q))
  ), [emails, tipoForn, filial, mes, q]);

  // Prévias: uma linha por fornecedor + filial + mês (junta as respostas da mesma conversa)
  const previas = useMemo(() => {
    const g = new Map();
    for (const e of base.filter(x => (x.tipos || []).includes('previa'))) {
      const k = `${e.fornecedorId || e.fornecedor}|${e.filial}|${e.mesRef}`;
      if (!g.has(k)) g.set(k, { chave: k, fornecedor: e.fornecedor, tipoFornecedor: e.tipoFornecedor, filial: e.filial, mesRef: e.mesRef, emails: [] });
      g.get(k).emails.push(e);
    }
    return [...g.values()].map(x => {
      const ord = x.emails.sort((a, b) => a.recebidoEm.localeCompare(b.recebidoEm));
      const primeira = ord.find(e => !e.resposta) || ord[0];
      const notaDoMes = emails.find(n => (n.tipos || []).includes('nota') && (n.fornecedorId || n.fornecedor) === (primeira.fornecedorId || primeira.fornecedor)
        && n.mesRef === x.mesRef && (!x.filial || !n.filial || n.filial === x.filial || n.filial === 'Ambas'));
      return { ...x, chegou: primeira.recebidoEm, ultima: ord[ord.length - 1].recebidoEm, mensagens: ord.length, assunto: primeira.assuntoBase, anexos: ord.flatMap(e => e.anexos || []), notaDoMes };
    }).sort((a, b) => b.chegou.localeCompare(a.chegou));
  }, [base, emails]);

  // Notas e vencimentos: uma linha por vencimento (boleto, parcela da NF-e ou data escrita no e-mail)
  const vencimentos = useMemo(() => {
    const out = [];
    for (const e of base.filter(x => (x.tipos || []).some(t => t === 'nota' || t === 'cobranca'))) {
      const nf = (e.notas || [])[0];
      const numero = nf?.numero || e.nfAssunto || '';
      const venc = e.vencimentos || [];
      if (!venc.length) { out.push({ chave: `${e.id}|`, e, numero, valor: nf?.valor ?? null, vencimento: '' }); continue; }
      for (const v of venc) {
        const bol = (e.boletos || []).find(b => b.vencimento === v);
        const parc = (nf?.parcelas || []).find(p => p.vencimento === v);
        out.push({ chave: `${e.id}|${v}`, e, numero, valor: bol?.valor ?? parc?.valor ?? (venc.length === 1 ? nf?.valor ?? null : null), vencimento: v });
      }
    }
    // mesma nota/boleto repetido em vários e-mails (respostas, encaminhamentos): fica o primeiro
    const vistos = new Set();
    return out.sort((a, b) => a.e.recebidoEm.localeCompare(b.e.recebidoEm)).filter(x => {
      const k = `${x.e.fornecedorId || x.e.fornecedor}|${x.numero}|${x.vencimento}|${x.valor}`;
      if ((x.numero || x.vencimento) && vistos.has(k)) return false;
      vistos.add(k); return true;
    }).filter(x => verPagos || !x.vencimento || prazo(x.vencimento).d >= -45)
      .sort((a, b) => (a.vencimento || '9999').localeCompare(b.vencimento || '9999'));
  }, [base, verPagos]);

  const meses = useMemo(() => [...new Set(emails.map(e => e.mesRef).filter(Boolean))].sort().reverse(), [emails]);
  const tiposForn = useMemo(() => [...new Set(emails.map(e => e.tipoFornecedor).filter(Boolean))].sort(), [emails]);
  const ultimaLeitura = emails.reduce((m, e) => (e.lidoEm > m ? e.lidoEm : m), '');

  const th = { padding: '9px 10px', textAlign: 'left', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.04em', color: P.muted, background: P.bg, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', verticalAlign: 'top', borderTop: '1px solid #f1f5f9', fontSize: '0.82rem' };
  const sel = { padding: '7px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.82rem' };
  const botaoVisao = (k, l, n) => (
    <button key={k} onClick={() => setVisao(k)} style={{ border: `1px solid ${visao === k ? P.primary : P.border}`, background: visao === k ? '#eef2ff' : P.white, color: visao === k ? P.primary : P.muted, borderRadius: 999, padding: '6px 14px', fontSize: '0.8rem', fontWeight: visao === k ? 800 : 600, cursor: 'pointer' }}>
      {l}{typeof n === 'number' ? ` · ${n}` : ''}
    </button>
  );
  const vencidos = vencimentos.filter(x => x.vencimento && prazo(x.vencimento).d < 0).length;
  const vence7 = vencimentos.filter(x => x.vencimento && prazo(x.vencimento).d >= 0 && prazo(x.vencimento).d <= 7).length;

  if (carregando) return <div style={{ color: P.muted }}>Carregando e-mails…</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
        <div style={{ fontSize: '0.8rem', color: P.muted }}>
          Caixa <strong>nucleo.adm2@vitalcarehc.com.br</strong> · {emails.length} e-mails financeiros lidos · última leitura {ultimaLeitura ? new Date(ultimaLeitura).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
        </div>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {atualizando && <span style={{ fontSize: '0.78rem', color: P.muted }}>{atualizando}</span>}
          <button onClick={atualizarAgora} disabled={!!atualizando} style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '7px 12px', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: '0.8rem', fontWeight: 600 }}>
            <RefreshCw size={14} /> Buscar e-mails novos
          </button>
        </span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {botaoVisao('previas', '📥 Prévias recebidas', previas.length)}
        {botaoVisao('vencimentos', '🧾 Notas e vencimentos', vencimentos.length)}
        {botaoVisao('tudo', '🗂 Tudo (relatórios, cobranças…)', base.length)}
        {visao === 'vencimentos' && <span style={{ marginLeft: 8, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={chip('#fee2e2', '#991b1b')}>Vencidos: {vencidos}</span>
          <span style={chip('#ffedd5', '#9a3412')}>Vencem em 7 dias: {vence7}</span>
        </span>}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <label style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: -9999 }}>Buscar fornecedor, assunto ou nº da nota</span>
          <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Fornecedor, assunto ou nº da nota" style={{ ...sel, paddingLeft: 30, width: 230 }} />
        </label>
        <select value={tipoForn} onChange={e => setTipoForn(e.target.value)} style={sel}>
          <option value="foco">Equipamentos, gases, remoções, mobiliário e raio-X</option>
          <option value="">Todos os fornecedores</option>
          {tiposForn.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filial} onChange={e => setFilial(e.target.value)} style={sel}>
          <option value="">Homebaby e Vitalcare</option>
          <option value="Homebaby">Homebaby</option>
          <option value="Vitalcare">Vitalcare</option>
        </select>
        <select value={mes} onChange={e => setMes(e.target.value)} style={sel}>
          <option value="">Todos os meses de referência</option>
          {meses.map(m => <option key={m} value={m}>{fmtMes(m)}</option>)}
        </select>
        {visao === 'vencimentos' && <label style={{ fontSize: '0.8rem', color: P.muted, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={verPagos} onChange={e => setVerPagos(e.target.checked)} /> Mostrar vencidos há mais de 45 dias
        </label>}
      </div>

      <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          {visao === 'previas' && (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <thead><tr>{['Fornecedor', 'Filial', 'Mês de ref.', 'Prévia chegou em', 'Última mensagem', 'Nota do mês', 'Anexos'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {previas.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: P.muted, padding: 28 }}>Nenhuma prévia neste filtro.</td></tr>}
                {previas.map(x => (
                  <tr key={x.chave}>
                    <td style={{ ...td, fontWeight: 700 }}>{x.fornecedor}<div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 500 }}>{x.tipoFornecedor}</div><div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 500, maxWidth: 320 }}>{x.assunto}</div></td>
                    <td style={td}>{x.filial || '—'}</td>
                    <td style={td}>{fmtMes(x.mesRef)}</td>
                    <td style={td}>{fmtData(x.chegou)}</td>
                    <td style={td}>{fmtData(x.ultima)}<div style={{ fontSize: '0.72rem', color: P.muted }}>{x.mensagens} mensage{x.mensagens === 1 ? 'm' : 'ns'}</div></td>
                    <td style={td}>{x.notaDoMes ? <span style={chip('#dcfce7', '#166534')}>Chegou {fmtData(x.notaDoMes.recebidoEm)}</span> : <span style={chip('#f1f5f9', '#475569')}>Aguardando</span>}</td>
                    <td style={td}><Anexos lista={x.anexos} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {visao === 'vencimentos' && (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <thead><tr>{['Vencimento', 'Fornecedor', 'Filial', 'Nº da nota', 'Valor', 'Chegou em', 'Assunto', 'Anexos'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {vencimentos.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: P.muted, padding: 28 }}>Nenhuma nota/vencimento neste filtro.</td></tr>}
                {vencimentos.map(x => {
                  const p = prazo(x.vencimento);
                  return (
                    <tr key={x.chave}>
                      <td style={td}>{x.vencimento ? <>{fmtData(x.vencimento)}<div><span style={chip(p.fundo, p.cor)}>{p.rotulo}</span></div></> : <span style={{ color: '#94a3b8' }}>não informado</span>}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{x.e.fornecedor}<div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 500 }}>{x.e.tipoFornecedor}</div></td>
                      <td style={td}>{x.e.filial || '—'}</td>
                      <td style={td}>{x.numero || '—'}</td>
                      <td style={td}>{fmtValor(x.valor)}</td>
                      <td style={td}>{fmtData(x.e.recebidoEm)}</td>
                      <td style={{ ...td, maxWidth: 280, fontSize: '0.76rem', color: P.muted }}>{x.e.assuntoBase}</td>
                      <td style={td}><Anexos lista={x.e.anexos} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {visao === 'tudo' && (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <thead><tr>{['Chegou em', 'Fornecedor', 'Tipo', 'Filial', 'Mês', 'Assunto', 'Anexos'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {[...base].sort((a, b) => b.recebidoEm.localeCompare(a.recebidoEm)).map(e => (
                  <tr key={e.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(e.recebidoEm)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{e.fornecedor}<div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 500 }}>{e.tipoFornecedor}</div></td>
                    <td style={td}>{(e.tipos || []).map(t => <span key={t} style={{ ...chip('#eef2ff', '#3730a3'), marginRight: 4 }}>{TIPO_ROTULO[t] || t}</span>)}</td>
                    <td style={td}>{e.filial || '—'}</td>
                    <td style={td}>{fmtMes(e.mesRef)}</td>
                    <td style={{ ...td, maxWidth: 360 }}>{e.assunto}<div style={{ fontSize: '0.72rem', color: P.muted }}>{e.deNome || e.de}</div></td>
                    <td style={td}><Anexos lista={e.anexos} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
